// src/ui/overlays/StaminaBar.tsx — P3 D-28 / UI-03.
//
// Top-left HUD chip with a 144×8 px bar + muted name/role tag. Fill width
// comes from playerStamina; colour lerps amber → coral via the CSS
// `--fill-pct` custom property. Hidden when round.phase === 'ended'.
//
// WARNING-15 tier-isolation: this component MUST NOT import PROFILES. The
// display name is resolved inside GameScene's sim-tick emit (from the sim
// player entity's characterId + the profiles record) and arrives on the
// payload. P5 admin-CMS loading (CHAR-07) will swap the resolution source
// to a KV-backed fetch without changing this component.

import { useEffect, useState, type CSSProperties, type JSX } from 'react';

import { renderBus } from '../../render/bus';

import './stamina-bar.css';

interface StaminaState {
  phase: 'headstart' | 'active' | 'ended';
  stamina: number;
  role: 'hunter' | 'hider';
  characterDisplayName: string;
}

export function StaminaBar(): JSX.Element | null {
  const [state, setState] = useState<StaminaState | null>(null);

  useEffect(() => {
    return renderBus.on('sim-tick', (e) => {
      setState({
        phase: e.round.phase,
        stamina: e.playerStamina,
        // narrow: hunter|hider only — dogs/adults never drive the stamina bar.
        role: e.playerRole === 'hunter' ? 'hunter' : 'hider',
        characterDisplayName: e.characterDisplayName,
      });
    });
  }, []);

  if (!state || state.phase === 'ended') return null;

  // characterDisplayName passes through verbatim — the sim tier is authoritative
  // (WARNING-15). role is already lower-cased in the payload.
  const tag = `${state.characterDisplayName} · ${state.role}`;
  const pct = Math.max(0, Math.min(1, state.stamina));

  // Use a typed CSS-with-custom-property style object so TypeScript accepts
  // the `--fill-pct` key without a broad `as string` cast.
  const fillStyle: CSSProperties & Record<'--fill-pct', string> = {
    width: `${pct * 100}%`,
    '--fill-pct': pct.toString(),
  };

  return (
    <div
      className="stamina-bar"
      data-testid="stamina-bar"
      role="meter"
      aria-label="Stamina"
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="stamina-bar__track">
        <div className="stamina-bar__fill" style={fillStyle} />
      </div>
      <span className="stamina-bar__tag">{tag}</span>
    </div>
  );
}
